import React, { useState, useEffect } from 'react';
import { Download, FileText, Image, FileSpreadsheet, Search, Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { mockData } from '../data/mockData';

export const Recursos = () => {
  const [resources, setResources] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = ['all', 'Guías', 'Manuales', 'Instrumentos', 'Infografías'];

  useEffect(() => {
    fetch(`/data/resources.json?t=${Date.now()}`, {
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    })
      .then(res => res.json())
      .then(data => setResources(data))
      .catch(() => setResources([]));
  }, []);

  const getIcon = (type) => {
    switch (type) {
      case 'PDF':
        return FileText;
      case 'Excel':
        return FileSpreadsheet;
      case 'PNG':
        return Image;
      default:
        return FileText;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'PDF':
        return 'bg-red-100 text-red-800';
      case 'Excel':
        return 'bg-green-100 text-green-800';
      case 'PNG':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredResources = resources.filter(resource => {
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || resource.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1481627834876-b7833e8f5570?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80"
            alt="Educational resources and tools"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-burgundy-50/65 to-white/75"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-burgundy-900 mb-6">
              Recursos y Herramientas
            </h1>
            <p className="text-xl text-burgundy-700 mb-8 max-w-3xl mx-auto">
              Material especializado para implementar y evaluar el modelo de
              Universidades Promotoras de la Salud
            </p>
          </div>
        </div>
      </section>

      {/* Resource Categories Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">

            <Card className="text-center hover:shadow-lg transition-shadow duration-300">
              <CardContent className="pt-8">
                <FileText className="h-12 w-12 text-burgundy-700 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-burgundy-800 mb-2">Guías y Manuales</h3>
                <p className="text-gray-600 text-sm">Documentos orientadores para comités y evaluadores</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow duration-300">
              <CardContent className="pt-8">
                <FileSpreadsheet className="h-12 w-12 text-burgundy-700 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-burgundy-800 mb-2">Instrumentos</h3>
                <p className="text-gray-600 text-sm">Matrices, rúbricas y listas de verificación</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow duration-300">
              <CardContent className="pt-8">
                <Image className="h-12 w-12 text-burgundy-700 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-burgundy-800 mb-2">Infografías</h3>
                <p className="text-gray-600 text-sm">Material visual para socialización del modelo</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow duration-300">
              <CardContent className="pt-8">
                <Download className="h-12 w-12 text-burgundy-700 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-burgundy-800 mb-2">Descargas PDF</h3>
                <p className="text-gray-600 text-sm">Todos los recursos en formato descargable</p>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-8 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar recursos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-600" />
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filtrar por categoría" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(category => (
                    <SelectItem key={category} value={category}>
                      {category === 'all' ? 'Todas las categorías' : category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredResources.map((resource) => {
              const IconComponent = getIcon(resource.type);
              return (
                <Card key={resource.id} className="hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-burgundy-100 rounded-lg flex items-center justify-center">
                          <IconComponent className="h-6 w-6 text-burgundy-700" />
                        </div>
                        <div>
                          <Badge className={getTypeColor(resource.type)}>
                            {resource.type}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <CardTitle className="text-lg text-burgundy-800 mt-4">
                      {resource.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600 mb-4">{resource.description}</p>
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="text-burgundy-700 border-burgundy-300">
                        {resource.category}
                      </Badge>
                      <a
                        href={resource.downloadUrl}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button
                          size="sm"
                          className="bg-burgundy-700 hover:bg-burgundy-800 text-white flex items-center"
                          as="span"
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Descargar
                        </Button>
                      </a>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {filteredResources.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">
                No se encontraron recursos con los criterios seleccionados
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Additional Resources Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-burgundy-900 mb-6">
              Recursos Adicionales
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Materiales complementarios para fortalecer la implementación del modelo UPS
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="hover:shadow-lg transition-shadow duration-300">
              <CardHeader>
                <CardTitle className="text-xl text-burgundy-800">
                  Biblioteca Digital
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Acceso a investigaciones, artículos científicos y casos de estudio
                  sobre universidades promotoras de la salud a nivel internacional.
                </p>
                <Button className="bg-burgundy-700 hover:bg-burgundy-800 text-white">
                  Explorar Biblioteca
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow duration-300">
              <CardHeader>
                <CardTitle className="text-xl text-burgundy-800">
                  Webinars y Capacitaciones
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Programa de formación continua con expertos internacionales en
                  promoción de la salud universitaria y procesos de acreditación.
                </p>
                <Button className="bg-burgundy-700 hover:bg-burgundy-800 text-white">
                  Ver Calendario
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};